import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Btn, Icon, IconBtn, StatusPill, T, alpha } from "@v0y4ge/jrni-ui";
import type { UIEvent } from "../../lib/events-adapter";
import {
  toDate, toParts, friendlyTime, friendlyDate, friendlyTimeRange, friendlyCalendarDay, friendlyCalendarMonth,
  friendlyCalendarWeekday, friendlyCalendarWeekRange, friendlyCalendarHour, getDateTimeConfig, useDateTimeVersion,
} from "../../lib/datetime";
import { useNav } from "../../shell/router";
import { useT, t as i18nT } from "../../lib/i18n";
import { eventActions } from "./eventActions";
import { SHARE_SOURCES } from "../../lib/public-url";
import { EVENT_STATUS } from "./EventsInlineTable";

/** Events calendar — Day · Week · Month, in the My Calendar visual language
 *  (2026-10-02 rebuild; the Calendar Control board's toolbar, grid and right-hand
 *  sheet, re-made natively in the app's own components). Data: same as the list —
 *  the UIEvent array the caller passes (listEvents via useEvents); nothing new is read.
 *  Click an empty slot → new event there (designer, prefilled). Click an event → sheet. */

type CalView = "day" | "week" | "month";
const MS_DAY = 24 * 60 * 60 * 1000;
const HOURS = Array.from({ length: 15 }, (_, i) => 7 + i); // ui-config — 7am..9pm
const ROW_H = 56;
const TIME_W = 64;

/* Week start follows the configured locale (Intl weekInfo: 1 = Mon … 7 = Sun). */
function weekStartDay(): number {
  try {
    const L = new (Intl as unknown as { Locale: new (l: string) => { weekInfo?: { firstDay: number }; getWeekInfo?: () => { firstDay: number } } }).Locale(getDateTimeConfig().locale);
    const fd = (L.getWeekInfo ? L.getWeekInfo() : L.weekInfo)?.firstDay;
    return fd ? fd % 7 : 1;
  } catch { return 1; }
}
const civil = (y: number, m: number, d: number) => new Date(y, m, d, 12); // noon: day-safe across tz formatting
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function startOfWeek(d: Date, first: number): Date { const x = civil(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() - first + 7) % 7)); return x; }

function whenOf(ev: UIEvent): { date: Date; start: number; end: number } | null {
  const tz = ev.raw?.timezone || undefined;
  if (ev.raw?.startsAt) {
    const p = toParts(ev.raw.startsAt, tz);
    const e = ev.raw?.endsAt ? toParts(ev.raw.endsAt, tz) : null;
    if (p) return { date: civil(p.y, p.mo, p.d), start: p.h + p.mi / 60, end: e ? e.h + e.mi / 60 : p.h + p.mi / 60 + 1 };
  }
  const base = toDate(ev.date);
  if (!base) return null;
  const [s, e] = String(ev.time || "").split("—").map((x) => x.trim());
  const sd = toDate(`${ev.date} · ${s}`) || base;
  const ed = e ? toDate(`${ev.date} · ${e}`) : null;
  const dec = (d: Date) => d.getHours() + d.getMinutes() / 60;
  return { date: civil(base.getFullYear(), base.getMonth(), base.getDate()), start: dec(sd), end: ed ? dec(ed) : dec(sd) + 1 };
}
const hm = (v: number) => { const d = new Date(); d.setHours(Math.floor(v), Math.round((v % 1) * 60), 0, 0); return friendlyTime(d); };
function timeLabel(ev: UIEvent, w: { start: number; end: number }): string {
  if (ev.raw?.startsAt && ev.raw?.endsAt) return friendlyTimeRange(ev.raw.startsAt, ev.raw.endsAt, ev.raw.timezone || undefined);
  return `${hm(w.start)}–${hm(w.end)}`;
}
/* ISO wall-clock "YYYY-MM-DDTHH:mm", the date picker's canonical value shape (CC, 2026-10-05). */
export function designerWhen(d: Date, hourDec: number): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(Math.floor(hourDec))}:${p(Math.round((hourDec % 1) * 60))}`;
}

/* Status colour — block fill, edge and ink. */
function tone(status: string): { edge: string; fill: string; ink: string; dashed?: boolean; strike?: boolean } {
  switch (status) {
    case "live": return { edge: T.green1, fill: alpha(T.green1, 12), ink: T.n1 };
    case "draft": return { edge: T.n3, fill: T.n6, ink: T.n2, dashed: true };
    case "canceled": return { edge: T.red1, fill: alpha(T.red1, 8), ink: T.soft, strike: true };
    case "past": return { edge: T.n4, fill: T.n6, ink: T.soft };
    default: return { edge: T.p1, fill: alpha(T.p1, 10), ink: T.n1 };
  }
}

const navBtn: CSSProperties = { height: 36, minWidth: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.n4}`, background: T.white, color: T.n1, fontFamily: T.font, fontSize: 14, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" };

export function EventsInlineCalendar({ events, fill }: { events: UIEvent[]; onOpen?: (ev: UIEvent) => void; onOpenWeek?: () => void; fill?: boolean }) {
  const t = useT();
  const nav = useNav();
  useDateTimeVersion();
  const NOW = new Date();
  const TODAY = civil(NOW.getFullYear(), NOW.getMonth(), NOW.getDate());
  const nowH = NOW.getHours() + NOW.getMinutes() / 60;
  const first = weekStartDay();

  const [view, setView] = useState<CalView>("month"); // ui-local
  const [cursor, setCursor] = useState<Date>(TODAY); // ui-local
  const [sheet, setSheet] = useState<UIEvent | null>(null); // ui-local
  const gridRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (view !== "month" && gridRef.current) gridRef.current.scrollTop = ROW_H; }, [view]);

  const step = (dir: 1 | -1) => setCursor((c) => view === "day" ? addDays(c, dir) : view === "week" ? addDays(c, dir * 7) : civil(c.getFullYear(), c.getMonth() + dir, 1)); // ui-local
  const ws = startOfWeek(cursor, first);
  const days = view === "day" ? [cursor] : Array.from({ length: 7 }, (_, i) => addDays(ws, i));

  const W = new Map<string, { date: Date; start: number; end: number }>();
  events.forEach((ev) => { const w = whenOf(ev); if (w) W.set(ev.id, w); });
  const onDay = (d: Date) => events.filter((ev) => { const w = W.get(ev.id); return w && sameDay(w.date, d); }).sort((a, b) => W.get(a.id)!.start - W.get(b.id)!.start);

  const title = view === "day" ? friendlyCalendarDay(cursor) : view === "month" ? friendlyCalendarMonth(cursor) : friendlyCalendarWeekRange(days[0], days[6]);
  const countLabel = (n: number) => i18nT("events.calendar.eventCount.icu", "{count, plural, one {# event} other {# events}}", { count: n });

  /* Opens the event designer the way Add Event does ({ new: "event" }); nothing consumed the old
     { newEvent } params, so a click on the calendar landed on the journeys list (CC, 2026-10-05). */
  const createAt = (d: Date, h: number) => nav.go("newjourneys", { new: "event", start: designerWhen(d, h), end: designerWhen(d, Math.min(h + 1, 23.5)) });

  const seg = (id: CalView, label: string) => {
    const on = view === id;
    return <button key={id} type="button" onClick={() => setView(id)} aria-pressed={on}
      style={{ height: 30, padding: "0 12px", border: "none", borderRadius: 6, background: on ? T.p3 : "transparent", color: on ? T.p1 : T.n2, fontFamily: T.font, fontSize: 13, fontWeight: on ? 600 : 500, cursor: "pointer" }}>{label}</button>;
  };

  const block = (ev: UIEvent, style: CSSProperties, compact: boolean) => {
    const w = W.get(ev.id)!; const c = tone(ev.status);
    return (
      <div key={ev.id} role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setSheet(ev); }} onKeyDown={(e) => { if (e.key === "Enter") setSheet(ev); }}
        style={{ ...style, background: c.fill, border: `1.5px ${c.dashed ? "dashed" : "solid"} ${c.edge}`, borderRadius: 6, padding: compact ? "3px 8px" : "6px 8px", overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column", gap: 1, boxShadow: sheet?.id === ev.id ? `0 0 0 2px ${alpha(c.edge, 35)}` : "none" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: c.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: c.strike ? "line-through" : "none" }}>{ev.title}</span>
        {!compact ? <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{timeLabel(ev, w)}</span> : null}
      </div>
    );
  };

  const monthView = () => {
    const firstOfMonth = civil(cursor.getFullYear(), cursor.getMonth(), 1);
    const gs = startOfWeek(firstOfMonth, first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gs, i));
    const rows = cells[35].getMonth() === cursor.getMonth() ? 6 : 5;
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", borderBottom: `1px solid ${T.n5}`, position: "sticky", top: 0, background: T.white, zIndex: 1 }}>
          {cells.slice(0, 7).map((d, i) => <div key={i} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: "0.06em", borderLeft: i ? `1px solid ${T.n5}` : "none" }}>{friendlyCalendarWeekday(d)}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))" }}>
          {cells.slice(0, rows * 7).map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth(); const isToday = sameDay(d, TODAY);
            const list = onDay(d);
            return (
              <div key={i} role="button" tabIndex={0} title={t("events.inlineCalendar.clickToAddAnEventOnThisDay", "Click to add an event on this day")}
                onClick={() => createAt(d, 9)} onKeyDown={(e) => { if (e.key === "Enter") createAt(d, 9); }}
                style={{ minHeight: 112, padding: "8px 8px 10px", borderLeft: i % 7 ? `1px solid ${T.n5}` : "none", borderBottom: `1px solid ${T.n5}`, background: inMonth ? T.white : T.n6, cursor: "pointer", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "0 4px" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: isToday ? T.white : inMonth ? T.n1 : T.soft, background: isToday ? T.p1 : "transparent", borderRadius: 999, minWidth: isToday ? 24 : 0, height: isToday ? 24 : "auto", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{d.getDate()}</span>
                  {list.length ? <span style={{ fontSize: 12, color: T.soft }}>{countLabel(list.length)}</span> : null}
                </div>
                {list.slice(0, 3).map((ev) => {
                  const c = tone(ev.status); const w = W.get(ev.id)!;
                  return (
                    <div key={ev.id} role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setSheet(ev); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setSheet(ev); } }}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 5, background: c.fill, border: `1px ${c.dashed ? "dashed" : "solid"} ${alpha(c.edge, 55)}`, fontSize: 12, minWidth: 0, cursor: "pointer" }}>
                      <span style={{ fontSize: 11, color: T.soft, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{hm(w.start)}</span>
                      <span style={{ fontWeight: 600, color: c.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: c.strike ? "line-through" : "none" }}>{ev.title}</span>
                    </div>
                  );
                })}
                {list.length > 3 ? <button type="button" onClick={(e) => { e.stopPropagation(); setCursor(d); setView("day"); }} style={{ alignSelf: "flex-start", border: "none", background: "transparent", padding: "0 4px", fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.p1, cursor: "pointer" }}>+{list.length - 3} {t("events.inlineCalendar.more", "more")}</button> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const timeView = () => (
    <>
      <div style={{ display: "grid", gridTemplateColumns: `${TIME_W}px repeat(${days.length}, minmax(0,1fr))`, borderBottom: `1px solid ${T.n5}` }}>
        <div style={{ alignSelf: "end", padding: "0 8px 8px", fontSize: 11.5, color: T.soft, textAlign: "right" }}>{t("events.calendar.time", "Time")}</div>
        {days.map((d, i) => {
          const isToday = sameDay(d, TODAY); const n = onDay(d).length;
          return (
            <button key={i} type="button" onClick={() => { if (view === "week") { setCursor(d); setView("day"); } }}
              style={{ border: "none", borderLeft: `1px solid ${T.n5}`, borderTop: `3px solid ${isToday ? T.p1 : "transparent"}`, background: isToday ? alpha(T.p1, 6) : T.white, padding: "10px 8px 12px", textAlign: "center", fontFamily: T.font, cursor: view === "week" ? "pointer" : "default" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: isToday ? T.p1 : T.n1 }}>{friendlyCalendarWeekday(d)} {d.getDate()}</div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{n ? countLabel(n) : "\u00a0"}</div>
            </button>
          );
        })}
      </div>
      <div ref={gridRef} style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `${TIME_W}px repeat(${days.length}, minmax(0,1fr))`, position: "relative" }}>
          <div>
            {HOURS.map((h) => <div key={h} style={{ height: ROW_H, padding: "2px 10px 0 0", textAlign: "right", fontSize: 12, color: T.soft, fontVariantNumeric: "tabular-nums" }}>{friendlyCalendarHour(h)}</div>)}
          </div>
          {days.map((d, di) => {
            const isToday = sameDay(d, TODAY);
            const list = onDay(d);
            return (
              <div key={di} title={t("events.inlineCalendar.clickAnEmptySlotToAddAnEvent", "Click an empty slot to add an event")}
                onClick={(e) => { const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect(); const y = e.clientY - r.top; createAt(d, HOURS[0] + Math.max(0, Math.floor((y / ROW_H) * 2) / 2)); }}
                style={{ position: "relative", borderLeft: `1px solid ${T.n5}`, background: isToday ? alpha(T.p1, 3) : "transparent", cursor: "pointer" }}>
                {HOURS.map((h) => (
                  <div key={h} style={{ height: ROW_H, borderBottom: `1px solid ${T.n5}`, position: "relative" }}>
                    <div style={{ position: "absolute", left: 0, right: 0, top: ROW_H / 2, borderTop: `1px dashed ${T.n5}` }} />
                  </div>
                ))}
                {list.map((ev, k) => {
                  const w = W.get(ev.id)!; const lo = HOURS[0], hi = HOURS[HOURS.length - 1] + 1;
                  const s = Math.max(lo, w.start), e = Math.min(hi, w.end);
                  if (e <= s) return null;
                  // Overlaps share the column side by side.
                  const lane = list.filter((o, j) => j < k && W.get(o.id)!.end > w.start && W.get(o.id)!.start < w.end).length;
                  const lanes = 1 + list.filter((o, j) => j !== k && W.get(o.id)!.end > w.start && W.get(o.id)!.start < w.end).length;
                  const h = (e - s) * ROW_H - 3;
                  return block(ev, { position: "absolute", top: (s - lo) * ROW_H + 1, height: h, left: `calc(${(lane / lanes) * 100}% + 4px)`, width: `calc(${100 / lanes}% - 8px)` }, h < 44);
                })}
                {isToday && nowH >= HOURS[0] && nowH <= HOURS[HOURS.length - 1] + 1 ? (
                  <div style={{ position: "absolute", left: 0, right: 0, top: (nowH - HOURS[0]) * ROW_H, height: 2, background: T.p1, pointerEvents: "none", zIndex: 2 }}>
                    <span style={{ position: "absolute", left: -4, top: -3, width: 8, height: 8, borderRadius: 999, background: T.p1 }} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  const sheetRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "112px minmax(0,1fr)", gap: 12, padding: "12px 14px", borderTop: `1px solid ${T.n5}` }}>
      <span style={{ fontSize: 13, color: T.soft }}>{label}</span>
      <span style={{ fontSize: 14, color: T.n1, minWidth: 0 }}>{value}</span>
    </div>
  );

  const ev = sheet; const sw = ev ? W.get(ev.id) : null; const st = ev ? (EVENT_STATUS[ev.status] ?? EVENT_STATUS.upcoming) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, ...(fill ? { flex: 1, minHeight: 570 } : {}) }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button type="button" aria-label={i18nT("events.inlineCalendar.previousView.icu", "Previous {value0}", { value0: view })} onClick={() => step(-1)} style={{ ...navBtn, padding: 0 }}><Icon.chev dir="left" size={14} /></button>
        <button type="button" onClick={() => setCursor(TODAY)} style={navBtn}>{t("events.calendar.today", "Today")}</button>
        <button type="button" aria-label={i18nT("events.inlineCalendar.nextView.icu", "Next {value0}", { value0: view })} onClick={() => step(1)} style={{ ...navBtn, padding: 0 }}><Icon.chev dir="right" size={14} /></button>
        <h3 style={{ margin: "0 6px", fontSize: 18, fontWeight: 700, color: T.n1, whiteSpace: "nowrap" }}>{title}</h3>
        <div role="group" aria-label={t("events.list.view", "View")} style={{ display: "inline-flex", gap: 2, padding: 3, border: `1px solid ${T.n5}`, borderRadius: 9, background: T.white }}>
          {seg("day", t("events.calendar.day", "Day"))}{seg("week", t("events.calendar.week", "Week"))}{seg("month", t("events.calendar.month", "Month"))}
        </div>
      </div>

      <div style={{ background: T.white, border: `1px solid ${T.n5}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", ...(fill ? { flex: 1, minHeight: 520 } : { height: 640 }) }}>
        {view === "month" ? monthView() : timeView()}
      </div>

      {ev && sw && st ? (
        <>
          <div onClick={() => setSheet(null)} style={{ position: "fixed", inset: 0, zIndex: 59 }} />
          <aside role="dialog" aria-label={ev.title} style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 400, maxWidth: "100vw", background: T.white, borderLeft: `1px solid ${T.n5}`, boxShadow: T.shadowLg, zIndex: 60, display: "flex", flexDirection: "column", fontFamily: T.font }}>
            <div style={{ padding: "18px 20px 16px", borderBottom: `1px solid ${T.n5}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.soft, display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: tone(ev.status).edge }} />{t("events.calendar.sheetEyebrow", "Event")}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginTop: 4, textWrap: "pretty" } as CSSProperties}>{ev.title}</div>
                <div style={{ fontSize: 13, color: T.soft, marginTop: 4 }}>{friendlyDate(sw.date)} · {timeLabel(ev, sw)}</div>
              </div>
              <IconBtn label={t("common.actions.close", "Close")} onClick={() => setSheet(null)}><Icon.x size={16} /></IconBtn>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              <div style={{ border: `1px solid ${T.n5}`, borderRadius: 10, background: T.n6, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "112px minmax(0,1fr)", gap: 12, padding: "12px 14px" }}>
                  <span style={{ fontSize: 13, color: T.soft }}>{t("events.inlineTable.headers.status", "Status")}</span>
                  <span><StatusPill tone={st.tone} dot>{t(st.labelKey, st.label)}</StatusPill></span>
                </div>
                {ev.location ? sheetRow(t("events.inlineTable.headers.location", "Location"), ev.location) : null}
                {ev.host ? sheetRow(t("events.inlineTable.headers.host", "Host"), ev.host) : null}
                {sheetRow(t("events.inlineTable.headers.registered", "Registered"), ev.capacity > 0 ? `${ev.registered} / ${ev.capacity}` : `${ev.registered} / ∞`)}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 16 }}>
                {[
                  { icon: <Icon.settings size={14} />, label: t("events.inlineTable.actions.manageEvent", "Manage event"), go: () => nav.openEvent(ev) },
                  { icon: <Icon.eye size={14} />, label: t("events.inlineTable.actions.preview", "Preview"), go: () => nav.go("newjourneys", { journey: `event:${ev.id}`, preview: "1" }) },
                  { icon: <Icon.external size={14} />, label: t("events.inlineTable.actions.openPublicPage", "Open public page"), go: () => eventActions.openLandingPage(ev, nav) },
                  { icon: <Icon.link size={14} />, label: t("events.inlineTable.actions.copyPublicUrl", "Copy public URL"), go: () => eventActions.copyPublicUrl(ev, SHARE_SOURCES.eventsCalendar) },
                ].map((a) => (
                  <button key={a.label} type="button" onClick={a.go} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "none", borderRadius: 8, background: "transparent", color: T.n1, fontFamily: T.font, fontSize: 14, fontWeight: 500, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ color: T.soft, display: "inline-flex" }}>{a.icon}</span>{a.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.n5}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn variant="secondary" onClick={() => setSheet(null)}>{t("common.actions.close", "Close")}</Btn>
              <Btn onClick={() => eventActions.editEvent(ev, nav)}>{t("events.inlineTable.actions.editDetails", "Edit details")}</Btn>
            </div>
          </aside>
        </>
      ) : null}
    </div>
  );
}
